/**
 * BotAttentionSection
 *
 * Shared component for displaying bot attention priority data.
 * Used in both Executive Value Dashboard (Story 73) and AI Factory Dashboard (Story 74).
 *
 * Features:
 * - Priority matrix visualization
 * - Top priority items table with actions
 * - Trending categories chart
 * - AI assistant focus table
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Bot,
  TrendingUp,
  TrendingDown,
  Target,
  AlertTriangle,
  Eye,
  ExternalLink,
  Sparkles,
  CheckCircle2,
  Clock,
  Layers,
  DollarSign,
  RefreshCw
} from 'lucide-react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';

export interface BotPriorityItem {
  productId: string;
  productName: string;
  category: string;
  url?: string;
  botVisits30d: number;
  botTrendPct: number;
  botTypes?: string[];
  hasContent: boolean;
  marketDemand?: number;
  estimatedCpc?: number | null;
  productSearchTerm?: string | null;
  categorySearchTerm?: string | null;
  revenueEstimate?: number;
  priorityScore: number;
  priorityStatus: 'PRIORITY' | 'NEXT_BATCH' | 'OPTIMIZED' | 'DISCOVERY_ISSUE' | 'BACKLOG';
}

export interface BotAttentionSummary {
  priorityCount: number;
  nextBatchCount: number;
  optimizedCount: number;
  discoveryIssueCount: number;
  backlogCount: number;
  total: number;
}

export interface TrendingCategory {
  category: string;
  botVisits: number;
  trendPct: number;
  contentCoverage: number;
  productCount: number;
}

export interface AIAssistantFocus {
  queryPattern: string;
  botVisits: number;
  coverage: number;
  gap: number;
}

export interface DiscoveryProblem {
  productId: string;
  productName: string;
  hasContent: boolean;
  botVisits: number;
  likelyIssue: string;
}

export interface KeywordEnrichmentStatus {
  enriched: number;
  pending: number;
  total: number;
  coverage: number;
  lastUpdated: string | null;
}

interface BotAttentionSectionProps {
  priorityItems: BotPriorityItem[];
  summary: BotAttentionSummary;
  trendingCategories: TrendingCategory[];
  aiAssistantFocus: AIAssistantFocus[];
  discoveryProblems?: DiscoveryProblem[];
  variant: 'executive' | 'factory';
  onViewDetails?: (item: BotPriorityItem) => void;
  onQueueForGeneration?: (productIds: string[]) => void;
  isQueuing?: boolean;
  keywordEnrichmentStatus?: KeywordEnrichmentStatus;
  onTriggerKeywordEnrichment?: () => void;
  isEnriching?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  PRIORITY: '#ef4444',
  NEXT_BATCH: '#f97316',
  OPTIMIZED: '#22c55e',
  DISCOVERY_ISSUE: '#eab308',
  BACKLOG: '#6b7280'
};

const STATUS_LABELS: Record<string, string> = {
  PRIORITY: 'Priority',
  NEXT_BATCH: 'Next Batch',
  OPTIMIZED: 'Optimized',
  DISCOVERY_ISSUE: 'Discovery Issue',
  BACKLOG: 'Backlog'
};

export function BotAttentionSection({
  priorityItems,
  summary,
  trendingCategories,
  aiAssistantFocus,
  discoveryProblems = [],
  variant,
  onViewDetails,
  onQueueForGeneration,
  isQueuing = false,
  keywordEnrichmentStatus,
  onTriggerKeywordEnrichment,
  isEnriching = false
}: BotAttentionSectionProps) {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState('priority');

  const toggleItem = (productId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedItems(newSelected);
  };

  const toggleAll = () => {
    if (selectedItems.size === priorityItems.filter(i => !i.hasContent).length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(priorityItems.filter(i => !i.hasContent).map(i => i.productId)));
    }
  };

  const handleQueueSelected = () => {
    if (onQueueForGeneration && selectedItems.size > 0) {
      onQueueForGeneration(Array.from(selectedItems));
      setSelectedItems(new Set());
    }
  };

  // Prepare quadrant data for scatter chart
  const quadrantData = priorityItems.map(item => ({
    x: item.hasContent ? 1 : 0,
    y: item.botVisits30d,
    name: item.productName,
    status: item.priorityStatus
  }));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              Bot Attention Priority
            </CardTitle>
            <CardDescription>
              Products prioritized by bot visits and content status
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {/* Keyword Enrichment Status & Button */}
            {variant === 'factory' && onTriggerKeywordEnrichment && (
              <div className="flex items-center gap-3">
                {keywordEnrichmentStatus && (
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    <span>
                      CPC Data: {keywordEnrichmentStatus.coverage}%
                      ({keywordEnrichmentStatus.enriched.toLocaleString()}/{keywordEnrichmentStatus.total.toLocaleString()})
                    </span>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onTriggerKeywordEnrichment}
                  disabled={isEnriching}
                >
                  {isEnriching ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Enriching...
                    </>
                  ) : (
                    <>
                      <DollarSign className="h-4 w-4 mr-2" />
                      Enrich Keywords
                    </>
                  )}
                </Button>
              </div>
            )}
            {/* Queue for Generation Button */}
            {variant === 'factory' && selectedItems.size > 0 && (
              <Button
                onClick={handleQueueSelected}
                disabled={isQueuing}
              >
                {isQueuing ? 'Queuing...' : `Queue ${selectedItems.size} for Generation`}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-red-50 dark:bg-red-950/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-600">{summary.priorityCount}</div>
            <div className="text-xs text-muted-foreground">Priority</div>
          </div>
          <div className="bg-orange-50 dark:bg-orange-950/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-orange-600">{summary.nextBatchCount}</div>
            <div className="text-xs text-muted-foreground">Next Batch</div>
          </div>
          <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{summary.optimizedCount}</div>
            <div className="text-xs text-muted-foreground">Optimized</div>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-950/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-yellow-600">{summary.discoveryIssueCount}</div>
            <div className="text-xs text-muted-foreground">Discovery Issues</div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-950/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-600">{summary.backlogCount}</div>
            <div className="text-xs text-muted-foreground">Backlog</div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="priority">Priority Items</TabsTrigger>
            <TabsTrigger value="trending">Trending</TabsTrigger>
            <TabsTrigger value="ai">AI Assistants</TabsTrigger>
            <TabsTrigger value="issues">Issues</TabsTrigger>
          </TabsList>

          {/* Priority Items Tab */}
          <TabsContent value="priority" className="mt-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    {variant === 'factory' && (
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedItems.size === priorityItems.filter(i => !i.hasContent).length && priorityItems.filter(i => !i.hasContent).length > 0}
                          onCheckedChange={toggleAll}
                        />
                      </TableHead>
                    )}
                    <TableHead>Product</TableHead>
                    <TableHead>Search Term</TableHead>
                    <TableHead className="text-right">Searches</TableHead>
                    <TableHead className="text-right">CPC</TableHead>
                    <TableHead className="text-right">Bot Visits</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {priorityItems.slice(0, 20).map(item => (
                    <TableRow key={item.productId}>
                      {variant === 'factory' && (
                        <TableCell>
                          {!item.hasContent && (
                            <Checkbox
                              checked={selectedItems.has(item.productId)}
                              onCheckedChange={() => toggleItem(item.productId)}
                            />
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {item.hasContent ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-gray-400" />
                          )}
                          <span className="font-medium truncate max-w-[180px]" title={item.productName}>
                            {item.productName}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.productSearchTerm ? (
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-blue-600 truncate max-w-[150px]" title={item.productSearchTerm}>
                              {item.productSearchTerm}
                            </span>
                            {item.categorySearchTerm && (
                              <span className="text-xs text-muted-foreground truncate max-w-[150px]" title={item.categorySearchTerm}>
                                {item.categorySearchTerm}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {item.marketDemand ? item.marketDemand.toLocaleString() : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {item.estimatedCpc ? (
                          <span className="text-green-600">{item.estimatedCpc.toFixed(2)}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {item.botVisits30d.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          style={{
                            borderColor: STATUS_COLORS[item.priorityStatus],
                            color: STATUS_COLORS[item.priorityStatus]
                          }}
                        >
                          {STATUS_LABELS[item.priorityStatus]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {item.priorityScore.toFixed(1)}
                      </TableCell>
                      <TableCell>
                        {onViewDetails && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onViewDetails(item)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {priorityItems.length > 20 && (
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Showing top 20 of {priorityItems.length} items
              </p>
            )}
          </TabsContent>

          {/* Trending Categories Tab */}
          <TabsContent value="trending" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendingCategories.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="botVisits" name="Bot Visits" fill="#3b82f6" />
                    <Bar yAxisId="right" dataKey="contentCoverage" name="Content %" fill="#22c55e" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Visits</TableHead>
                      <TableHead className="text-right">Trend</TableHead>
                      <TableHead className="text-right">Coverage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trendingCategories.slice(0, 10).map(cat => (
                      <TableRow key={cat.category}>
                        <TableCell className="font-medium">{cat.category}</TableCell>
                        <TableCell className="text-right font-mono">{cat.botVisits.toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <span className={cat.trendPct > 0 ? 'text-green-600' : cat.trendPct < 0 ? 'text-red-600' : ''}>
                            {cat.trendPct > 0 ? '+' : ''}{cat.trendPct.toFixed(0)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-2">
                            <Progress value={cat.contentCoverage} className="w-16 h-2" />
                            <span className="text-xs">{cat.contentCoverage.toFixed(0)}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* AI Assistants Tab */}
          <TabsContent value="ai" className="mt-4">
            <div className="space-y-4">
              <Alert>
                <Sparkles className="h-4 w-4" />
                <AlertDescription>
                  AI assistants (GPTBot, ClaudeBot) are actively crawling your catalog.
                  Ensure these products have optimized content.
                </AlertDescription>
              </Alert>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Query Pattern</TableHead>
                    <TableHead className="text-right">AI Bot Visits</TableHead>
                    <TableHead className="text-right">Coverage</TableHead>
                    <TableHead className="text-right">Gap</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aiAssistantFocus.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No AI assistant activity detected
                      </TableCell>
                    </TableRow>
                  ) : (
                    aiAssistantFocus.map(item => (
                      <TableRow key={item.queryPattern}>
                        <TableCell className="font-medium">{item.queryPattern}</TableCell>
                        <TableCell className="text-right font-mono">{item.botVisits.toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Progress value={item.coverage} className="w-16 h-2" />
                            <span className="text-xs">{item.coverage.toFixed(0)}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={item.gap > 50 ? 'destructive' : item.gap > 20 ? 'default' : 'secondary'}>
                            {item.gap.toFixed(0)}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Discovery Issues Tab */}
          <TabsContent value="issues" className="mt-4">
            <div className="space-y-4">
              {discoveryProblems.length === 0 ? (
                <Alert>
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <AlertDescription>
                    No discovery issues detected. Products with content are being crawled.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      {discoveryProblems.length} products have content but are not being crawled.
                      This may indicate sitemap or internal linking issues.
                    </AlertDescription>
                  </Alert>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Bot Visits</TableHead>
                        <TableHead>Likely Issue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {discoveryProblems.slice(0, 10).map(item => (
                        <TableRow key={item.productId}>
                          <TableCell className="font-medium">{item.productName}</TableCell>
                          <TableCell className="text-right font-mono">{item.botVisits}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.likelyIssue.replace(/_/g, ' ')}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

export default BotAttentionSection;
